<script lang="ts" setup>
import { HOME_SWIPER_OPTIONS } from '~/resources/game'
import { SECTION_GAMES_LIMIT } from '~/constants'
import { PROVIDER_CASINO_ALL } from '~/resources'
import type { GameItem } from '@kira-dancer/nuxt-common'
import { useForm } from 'vee-validate'
import type { DropdownOption } from '~/types/app'
import type { CasinoProviderGame } from '~/types/game'
import {
  CasinoGroupEnum,
  CasinoProviderSlugEnum,
  KeyEnum,
  SlidersPerView,
} from '~/enums'

import cacheManager from '#lib/utils/cache-manager'
import { useCasinoProvider } from '~/composables/game/use-casino-provider'

const currentProvider = ref(PROVIDER_CASINO_ALL[0].slug)
const { isMobile } = useDevice()
const { casinoProviders } = useCasinoProvider()
const isLoading = ref(false)
const isFirstLoad = ref(true)

const swiperOptions = {
  ...HOME_SWIPER_OPTIONS,
  loop: false,
  slidesPerView: isMobile ? 3.2 : SlidersPerView.Medium,
  spaceBetween: isMobile ? 12 : 24,
  slidesOffsetAfter: isMobile ? 16 : 0,
}

const { setFieldValue } = useForm({
  initialValues: {
    casinoProviders: currentProvider.value,
  },
})

const { fetchLiveCasino } = useCasino({
  gameLimit: SECTION_GAMES_LIMIT,
})

const getProviderParam = () => {
  return currentProvider.value !== CasinoProviderSlugEnum.All
    ? { prov: currentProvider.value }
    : { prov: '' }
}

const fetchHomeCasinoGames = async () => {
  isLoading.value = true
  const cachedData = cacheManager.get(KeyEnum.HomeLiveCasino)
  if (
    cachedData &&
    currentProvider.value === CasinoProviderSlugEnum.All &&
    isFirstLoad.value
  ) {
    isLoading.value = false
    return cachedData
  }
  try {
    const dataGames = await fetchLiveCasino({
      ...getProviderParam(),
      limit: SECTION_GAMES_LIMIT,
      paged: 0,
      groups: CasinoGroupEnum.TopGame,
    })
    cacheManager.set(KeyEnum.HomeLiveCasino, dataGames)
    return dataGames
  } catch {
    return []
  } finally {
    isLoading.value = false
  }
}

const { data: homeCasinoGames, refresh } = useAsyncData(
  KeyEnum.HomeLiveCasino,
  fetchHomeCasinoGames,
)

watch(
  () => currentProvider.value,
  () => {
    if (isFirstLoad.value) {
      isFirstLoad.value = false
      return
    }
  },
)

const getProviderImage = (provider: string) => {
  const iconImgUrl = '/assets/images/live-casino'
  const globImages: GlobImageI = import.meta.glob(
    '~/assets/images/live-casino/*',
    {
      eager: true,
    },
  )

  const filePath = Object.keys(globImages).find((key) => key.includes(provider))
  const fileExtension = filePath?.split('.').pop() ?? 'png'

  return getImageURL(globImages, provider, iconImgUrl, fileExtension)
}

const changeProvider = (provider: string): void => {
  currentProvider.value = provider
  refresh()
}

const providerOptions = computed<DropdownOption[]>(() =>
  [...PROVIDER_CASINO_ALL, ...casinoProviders.value].map(
    (item: CasinoProviderGame) => ({
      key: item.slug?.toString() ?? '',
      title: item.nameMobile ?? item.name ?? '',
      icon: item.slug ?? '',
    }),
  ),
)

watch(
  () => currentProvider.value,
  (value) => {
    setFieldValue('casinoProviders', value)
  },
  { immediate: true },
)
</script>
<template>
  <BaseSection
    title="Live Casino"
    icon="icon-casino"
    custom-class="casino-section"
  >
    <div v-if="isLoading" class="loading-spin" />
    <BaseSwiper
      v-if="homeCasinoGames?.length && !isLoading"
      :items="homeCasinoGames"
      :options="swiperOptions"
    >
      <template #item="{ item }: { item: GameItem }">
        <CommonGameItem
          :item="item"
          is-casino
          :casino-game-limit="SECTION_GAMES_LIMIT"
        />
      </template>
    </BaseSwiper>
    <div v-if="!homeCasinoGames?.length" class="casino__empty">
      <img
        src="/assets/images/game/icon-casino-empty.webp"
        alt="icon empty"
        width="96"
        height="96"
      />
      Hệ thống đang cập nhật, vui lòng thử lại sau
    </div>
    <template #right-action>
      <FormDropdown
        :options="providerOptions"
        :clearable="false"
        name="casinoProviders"
        content-class="casino-providers-filter"
        :scroll-behavior="isMobile ? 'close' : 'none'"
      >
        <template #item="{ item, props: itemProps }">
          <div
            :class="[
              'item-dropdown',
              'bank-dropdown-item',
              {
                'bank-dropdown-item--selected':
                  currentProvider === item.raw.key,
              },
              { 'bank-dropdown-item--hidden': item.value === '' },
            ]"
            v-bind="itemProps"
            title=""
            @click="changeProvider(item.raw.key)"
          >
            <img
              class="logo-casino-provider"
              :alt="item.title"
              :src="
                getProviderImage(
                  currentProvider === item.raw.key
                    ? `${item.value}-active-dropdown`
                    : `${item.raw.icon}-dropdown`,
                )
              "
              width="32"
              height="32"
            />
            <img
              class="logo-casino-provider hover active"
              :alt="item.title"
              :src="getProviderImage(`${item.value}-active-dropdown`)"
              width="32"
              height="32"
            />
            <span class="bank-dropdown-item__title line-limit-1">{{
              item.title
            }}</span>
          </div>
        </template>
        <template #selection="{ item }">
          <div class="bank-dropdown-selection-item">
            <img
              class="logo-casino-provider"
              :alt="item.title"
              :src="getProviderImage(`${item.value}-active-dropdown`)"
              width="32"
              height="32"
            />
            <span class="bank-dropdown-selection-item__title line-limit-1">{{
              item.title
            }}</span>
          </div>
        </template>
      </FormDropdown>
    </template>
  </BaseSection>
</template>
<style scoped lang="scss">
@import '~/assets/styles/components/desktop/home/casino-section.scss';
</style>
<style lang="scss">
@import '~/assets/styles/components/responsive/desktop/home/casino-section.scss';
</style>
